﻿import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Stack,
  FormControlLabel,
  TextField,
  IconButton,
  InputLabel,
  Autocomplete,
  Container,
} from "@mui/material";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectConnectionProfile } from "@/features/settings/settingsSlice";
import "@/components/ConnectionsBar/ConnectionsBar.css";
import "./WorkflowSettingsPopup.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { SettingsCard } from "./WorkflowSettingsPopup";
import { createAlert } from "@/features/homepage/alertSliceList";
import {
  ShepCheckbox,
  ShepTab,
  ShepTabs,
} from "@/components/GenericComponents/ShepComponents";
import ScriptEditor from "@/components/GenericComponents/ScriptEditor";
import { ShepTooltip } from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import DeleteIcon from "@/assets/img/icons/Delete/Line.svg?react";
import {
  ShepDataGridPro,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import PreviewWorkflowScript from "./PreviewWorkflowScript";

const WorkflowScriptSettings = ({ isGlobal, onClose, workflowName }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const dispatch = useDispatch();
  const selectedOperationId = useSelector(selectWorkflow);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(true);
  const [sourceArtifactFields, setSourceArtifactFields] = useState(null);
  const [targetArtifactFields, setTargetArtifactFields] = useState(null);
  let connectionProfile = useSelector(selectConnectionProfile);
  const [editingRecord, setEditingRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [globalScripts, setGlobalScripts] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [username, setUsername] = useState(null);
  const [globalPreferences, setGlobalPreferences] = useState(new Map());
  const [enabledPreferences, setEnabledPreferences] = useState(new Map());
  const [orderChanged, setOrderChanged] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [sourceAutocompletions, setSourceAutocompletions] = useState(null);
  const [targetAutocompletions, setTargetAutocompletions] = useState(null);
  const [autocompletions, setAutocompletions] = useState(null);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [script, setScript] = useState("");
  const [timing, setTiming] = useState(0);
  const [dateUpdated, setDateUpdated] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [previewRows, setPreviewRows] = useState([]);

  useEffect(() => {
    const merged = [...globalScripts, ...rows];
    merged.sort((a, b) => (a.Index ?? Infinity) - (b.Index ?? Infinity));
    setDataRows(merged);
    setPreviewRows(merged);
  }, [rows, globalScripts]);

  useEffect(() => {
    if (tabValue === 1) {
      setFilteredRows(dataRows.filter((r) => Number(r.Timing) === 1));
    } else {
      setFilteredRows(dataRows.filter((r) => Number(r.Timing) === 0));
    }
  }, [tabValue, dataRows]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    getSchemaForAutocomplete();
    loadScripts();
    getUser();
  }, [connectionProfile]);

  useEffect(() => {
    getAutocompletions();
  }, [sourceArtifactFields, targetArtifactFields]);

  useEffect(() => {
    if (Number(timing) === 0 && sourceAutocompletions) {
      setAutocompletions(sourceAutocompletions);
    } else if (Number(timing) === 1 && targetAutocompletions) {
      setAutocompletions([...targetAutocompletions, ...sourceAutocompletions]);
    }
  }, [timing, sourceAutocompletions, targetAutocompletions]);

  const getAutocompletions = () => {
    const completionSourceList = [];
    if (sourceArtifactFields && sourceArtifactFields.Paths) {
      sourceArtifactFields.Paths.forEach((object) => {
        if (
          targetArtifactFields &&
          targetArtifactFields.ArtifactName ===
            sourceArtifactFields.ArtifactName
        ) {
          completionSourceList.push(
            `Source${sourceArtifactFields.ArtifactName}.${object}`,
          );
        } else {
          completionSourceList.push(
            `${sourceArtifactFields.ArtifactName}.${object}`,
          );
        }
      });
    }

    setSourceAutocompletions(completionSourceList);
    const completionTargetList = [];
    if (targetArtifactFields && targetArtifactFields.Paths) {
      targetArtifactFields.Paths.forEach((object) => {
        if (
          sourceArtifactFields &&
          sourceArtifactFields.ArtifactName ===
            targetArtifactFields.ArtifactName
        ) {
          completionTargetList.push(
            `Target${targetArtifactFields.ArtifactName}.${object}`, // source and target artifact are the same type, so add label to differentiate them
          );
        } else {
          completionTargetList.push(
            `${targetArtifactFields.ArtifactName}.${object}`,
          );
        }
      });
    }
    setTargetAutocompletions(completionTargetList);
  };

  const loadScripts = async () => {
    const prefMap = {};
    const orderMap = {};
    await axios
      .get("/operations/workflow-scripts", {
        params: {
          operationId: selectedOperationId,
          connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
          isGlobal: isGlobal,
        },
      })
      .then((response) => {
        const { scripts, globalPreferences, order } = response.data;

        if (globalPreferences) {
          globalPreferences.forEach((p) => (prefMap[p.Id] = p));
        }
        if (order) {
          order.forEach((p) => (orderMap[p.Id] = p));
        }

        if (scripts) {
          const records = scripts.map((x) => ({
            id: x.Id,
            Name: x.Name,
            LastUpdated: x.LastUpdated,
            UserName: x.UserName,
            IsEnabled: Boolean(x.IsEnabled),
            Script: x.Script,
            Timing: x.Timing,
            isGlobal: isGlobal ?? true,
            UseGlobal: null,
            Index: orderMap[x.Id]?.Index,
          }));

          setRows(records);
        } else {
          setRows([]);
        }
      });

    if (!isGlobal) {
      await axios
        .get("/operations/workflow-scripts", {
          params: {
            operationId: selectedOperationId,
            connectionPairId: -1,
            isGlobal: true,
          },
        })
        .then((response) => {
          var results = response.data.scripts;
          if (results) {
            const records = results.map((x) => ({
              id: x.Id,
              Name: x.Name,
              LastUpdated: x.LastUpdated,
              UserName: x.UserName,
              IsEnabled: Boolean(x.IsEnabled),
              Script: x.Script,
              Timing: x.Timing,
              isGlobal: Boolean(true),
              UseGlobal: Boolean(prefMap[x.Id]?.UseGlobal),
              Index: orderMap[x.Id]?.Index,
            }));

            setGlobalScripts(records);
          }
        });
    }
  };

  const handleSave = async () => {
    if ((!name && script) || (!script && name)) {
      dispatch(
        createAlert({
          status: "Info",
          alertType: "single",
          message: "Please fill out all the fields",
        }),
      );
      return;
    }
    if (
      isEditing &&
      name &&
      script &&
      (name !== editingRecord.Name ||
        script !== editingRecord.Script ||
        Number(timing) !== Number(editingRecord.Timing))
    ) {
      try {
        await axios.put(
          `/operations/connection-profiles/${selectedOperationId}/edit-script`,
          {
            Id: id,
            Name: name,
            Timing: Number(timing),
            Script: script,
            UserName: username,
          },
          {
            params: {
              operationId: selectedOperationId,
              connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
              isGlobal: isGlobal,
            },
          },
        );
      } catch (error) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error editing script: " + error.response.data,
          }),
        );
        return false;
      }
    } else if (isAdding && name && script) {
      try {
        await axios.post(
          `/operations/connection-profiles/${selectedOperationId}/add-script`,
          {
            Name: name,
            Timing: Number(timing),
            Script: script,
            IsEnabled: true,
            UserName: username,
          },
          {
            params: {
              operationId: selectedOperationId,
              connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
              isGlobal: isGlobal,
            },
          },
        );
      } catch (error) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error saving script: " + error.response.data,
          }),
        );
        return false;
      }
    }
    if (orderChanged) {
      let preIdx = 1;
      let postIdx = 1;
      const payload = dataRows.map((row) => {
        const index = Number(row.Timing) === 0 ? preIdx++ : postIdx++;

        return {
          Id: row.id,
          Timing: Number(row.Timing),
          Index: index,
        };
      });

      try {
        await axios.post(
          `/operations/connection-profiles/${selectedOperationId}/reorder-scripts`,
          payload,
          {
            params: {
              operationId: selectedOperationId,
              connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
              isGlobal: isGlobal,
            },
          },
        );
        setOrderChanged(false);
      } catch (error) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error saving settings",
          }),
        );
        return false;
      }
    }
    if (enabledPreferences.size > 0) {
      const updates = Array.from(enabledPreferences.entries()).map(
        ([Id, IsEnabled]) => ({
          Id,
          IsEnabled,
        }),
      );

      try {
        await axios.put(`/operations/enable-scripts`, updates, {
          params: {
            operationId: selectedOperationId,
            connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
            isGlobal: isGlobal,
          },
        });
        setEnabledPreferences(new Map());
      } catch (error) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error saving settings",
          }),
        );
        return false;
      }
    }
    if (globalPreferences.size > 0) {
      const updates = Array.from(globalPreferences.entries()).map(
        ([Id, UseGlobal]) => ({
          Id,
          UseGlobal,
        }),
      );

      try {
        await axios.put(`/operations/global-script-preference`, updates, {
          params: {
            operationId: selectedOperationId,
            connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
          },
        });
        setGlobalPreferences(new Map());
      } catch (error) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error saving settings",
          }),
        );
        return false;
      }
    }
    loadScripts();
    setIsEditing(false);
    setIsAdding(true);
    setId("");
    setName("");
    setScript("");
    dispatch(
      createAlert({
        status: "Success",
        alertType: "single",
        message: "Settings saved successfully!",
      }),
    );
  };

  const getUser = async () => {
    axios
      .get(`/account/user-profile`)
      .then((response) => {
        if (response.data == null) {
          return;
        }
        setUsername(response.data.LeapFrogBetaUser.UserName);
      })
      .catch((err) => console.log(err));
  };

  const handleReorder = ({ row, oldIndex, targetIndex }) => {
    const timing = Number(row.Timing);

    const timingRows = dataRows.filter((r) => Number(r.Timing) === timing);

    const updatedTimingRows = [...timingRows];
    const [moved] = updatedTimingRows.splice(oldIndex, 1);
    updatedTimingRows.splice(targetIndex, 0, moved);

    const updatedAll = dataRows
      .filter((r) => r.Timing !== timing)
      .concat(updatedTimingRows);
    setDataRows(updatedAll);
    setOrderChanged(true);
  };

  const handleClose = () => {
    if (
      orderChanged ||
      globalPreferences.size > 0 ||
      enabledPreferences.size > 0 ||
      (isEditing &&
        (name !== editingRecord.Name ||
          script !== editingRecord.Script ||
          Number(timing) !== Number(editingRecord.Timing))) ||
      (isAdding && (name || script))
    ) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "There are unsaved changes remaining, do you wish to close the window?",
          buttonLabel: "Close",
          onButtonClick: () => onClose(),
          buttonLabel2: "Cancel",
        }),
      );
    } else {
      onClose();
    }
  };

  const deleteScript = async (recordId) => {
    axios
      .delete(
        `/operations/connection-profiles/${selectedOperationId}/delete-script`,
        {
          params: {
            operationId: selectedOperationId,
            connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
            isGlobal: isGlobal,
            recordId: recordId,
          },
        },
      )
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: "Script deleted successfully!",
          }),
        );
        loadScripts();
        // deleting a record selects it so refresh the editor
        setIsAdding(true);
        setIsEditing(false);
        setId("");
        setName("");
        setScript("");
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Error deleting script",
          }),
        );
      });
  };

  const getSchemaForAutocomplete = () => {
    return axios
      .get("/operations/operation-artifact-types", {
        params: {
          operationId: selectedOperationId,
        },
      })
      .then((response) => {
        const sourceArtifactType = response.data.SourceArtifactType;
        const targetArtifactType = response.data.TargetArtifactType;

        return Promise.all([
          axios.get("/mapschema/artifact-schema-props", {
            params: { artifactType: sourceArtifactType },
          }),
          axios.get("/mapschema/artifact-schema-props", {
            params: { artifactType: targetArtifactType },
          }),
          axios.get("/mapschema/connection-custom-fields"),
        ]);
      })
      .then(
        ([
          sourceSchemaResult,
          targetSchemaResult,
          connectionCustomFieldsResult,
        ]) => {
          const connectionCustomFields =
            connectionCustomFieldsResult.data || [];
          const sourceData = sourceSchemaResult.data;
          sourceData.Paths = [
            ...(sourceData.Paths || []),
            ...connectionCustomFields, // Append the master list of connection custom fields
          ];
          setSourceArtifactFields(sourceData);

          const targetData = targetSchemaResult.data;
          targetData.Paths = [
            ...(targetData.Paths || []),
            ...connectionCustomFields, // Append the exact same master list of connection custom fields
          ];
          setTargetArtifactFields(targetData);
        },
      )
      .catch((error) => {
        console.error("Error fetching data: ", error);
        throw error;
      });
  };

  const columns = [
    {
      field: "Name",
      headerName: "Script Name",
      width: 150,
      sortable: false,
    },
    {
      field: "IsEnabled",
      headerName: "Enabled",
      width: 110,
      sortable: false,
      renderCell: (params) => {
        return (
          <FormControlLabel
            control={
              <ShepCheckbox
                checked={params.row.IsEnabled}
                disabled={!isGlobal && params.row.isGlobal}
                onChange={(event) => {
                  const ischecked = event.target.checked;

                  setDataRows((prev) =>
                    prev.map((r) =>
                      r.id === params.row.id
                        ? { ...r, IsEnabled: ischecked }
                        : r,
                    ),
                  );

                  setEnabledPreferences((prev) => {
                    const copy = new Map(prev);
                    copy.set(params.row.id, ischecked);
                    return copy;
                  });
                }}
              />
            }
            style={{ marginLeft: 5, minWidth: 180 }}
          />
        );
      },
    },
    {
      width: 150,
      resizeable: true,
      headerName: !isGlobal ? "Use Global" : "",
      renderCell: (params) => {
        if (!params.row.isGlobal || isGlobal) return null;
        return (
          <Box sx={{ overflow: "visible" }}>
            <FormControlLabel
              control={
                <ShepCheckbox
                  checked={params.row.UseGlobal}
                  onChange={(event) => {
                    const ischecked = event.target.checked;
                    setDataRows((prev) =>
                      prev.map((r) =>
                        r.id === params.row.id
                          ? { ...r, UseGlobal: ischecked }
                          : r,
                      ),
                    );

                    setGlobalPreferences((prev) => {
                      const copy = new Map(prev);
                      copy.set(params.row.id, ischecked);
                      return copy;
                    });
                  }}
                />
              }
              style={{ marginLeft: 5, minWidth: 180 }}
            />
          </Box>
        );
      },
    },
    {
      field: "deleteAction",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isGlobal && !isGlobal) return null;
        return (
          <ShepTooltip title="Delete Script">
            <IconButton
              className="workflow-row" // Carries hover-only visibility
              size="small"
              onClick={() => {
                setEditingRecord(params.row);
                dispatch(
                  createAlert({
                    status: "Warning",
                    alertType: "result",
                    message: "Are you sure you want to delete this script?",
                    buttonLabel: "Delete",
                    onButtonClick: () => deleteScript(params.row.id),
                    buttonLabel2: "Cancel",
                  }),
                );
              }}
              disableRipple
            >
              <DeleteIcon />
            </IconButton>
          </ShepTooltip>
        );
      },
    },
  ];

  const options = [
    { label: "Before Processing", value: 0 },
    { label: "After Processing", value: 1 },
  ];

  const actions = [
    {
      label: "New",
      onClick: () => {
        setIsEditing(false);
        setIsAdding(true);
        setId("");
        setName("");
        setScript("");
        setDateUpdated("");
        setUpdatedBy("");
      },
    },
  ];

  return (
    <>
      {
        <Stack direction={"column"}>
          <>
            <SettingsCard
              title={"Script"}
              description={"Create scripts for pre and post processing."}
              className="config-options-card scheduler-settings-card"
            >
              <div className="grid-wrapper">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    marginTop: "-15px",
                  }}
                >
                  <ShepHorizontalMenu actions={actions} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <Container sx={{ width: "45%", minWidth: 0 }}>
                    <Stack style={{ paddingRight: "80px" }}>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: "var(--primary-main)",
                        }}
                      >
                        <ShepTabs
                          variant="primary"
                          value={tabValue}
                          onChange={handleTabChange}
                          sx={{
                            "& .MuiTab-root": {},
                          }}
                        >
                          <ShepTab
                            variant="primary"
                            label={"Before Processing"}
                            sx={{ width: "45%" }}
                          ></ShepTab>
                          <ShepTab
                            variant="primary"
                            label={"After Processing"}
                            sx={{ width: "45%" }}
                          ></ShepTab>
                        </ShepTabs>
                      </Box>
                      <ShepDataGridPro
                        isDataLoaded={true}
                        rows={filteredRows}
                        rowCount={filteredRows.length}
                        columns={columns}
                        columnHeaderHeight={78}
                        rowHeight={100}
                        whiteHeader={true}
                        checkboxSelection={false}
                        disableMultipleRowSelection={false}
                        disableColumnMenu={true}
                        disableColumnFilter={true}
                        disableRowSelectionOnClick={false}
                        disableColumnReorder={true}
                        hideFooterSelectedRowCount={true}
                        pagination
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 5, page: 1 },
                          },
                        }}
                        pageSizeOptions={[6]}
                        rowReordering={true}
                        onRowOrderChange={(params) => {
                          handleReorder(params);
                        }}
                        onRowClick={(params) => {
                          setIsEditing(true);
                          setIsAdding(false);
                          setId(params.row.id);
                          setName(params.row.Name);
                          setScript(params.row.Script);
                          setTiming(params.row.Timing);
                          setUpdatedBy(params.row.UserName);
                          setDateUpdated(params.row.LastUpdated);
                          setEditingRecord(params.row);
                        }}
                        sx={{
                          color: "var(--text-primary)",
                          fontSize: "var(--font-sm)",
                          fontFamily: "var(--font-family)",
                          borderTopLeftRadius: "12px",
                          borderTopRightRadius: "12px",
                          backgroundColor: "var(--default-light)",
                          border: "none",
                          "& .MuiDataGrid:hover": {
                            backgroundColor: "inherit",
                          },
                          "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                          },
                          "& .MuiDataGrid-row:hover": {
                            backgroundColor: "var(--tertiary-13)",
                          },
                          "& .Mui-selected": {
                            backgroundColor: "var(--primary-pale)",
                          },
                          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                            {
                              outline: "none",
                            },
                          "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                            {
                              opacity: 1,
                            },
                          "& .MuiDataGrid-columnHeaderTitle": {
                            fontSize: "var(--font-md)",
                            fontWeight: "var(--font-bold)",
                            borderLeft: "none",
                          },
                          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                            {
                              borderBottomLeftRadius: "4px",
                              borderBottomRightRadius: "4px",
                              backgroundColor: "var(--primary-pale)",
                              borderBottom: "none",
                              borderLeft: "none",
                            },
                          "& .MuiDataGrid-columnHeader:first-of-type": {
                            paddingLeft: "20px",
                          },
                          "& .MuiDataGrid-cell:first-of-type": {
                            paddingLeft: "20px",
                          },
                          "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                            opacity: 0,
                          },
                        }}
                      />
                    </Stack>
                  </Container>
                  <Container sx={{ width: "55%", minWidth: 0 }}>
                    <Stack sx={{ width: "100%" }}>
                      <Stack
                        flexDirection={"row"}
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginBottom: "20px",
                        }}
                      >
                        <Stack>
                          <InputLabel
                            className="conn-pair-options"
                            style={{
                              paddingRight: "10px",
                              paddingBottom: "5px",
                            }}
                          >
                            Script Name:
                          </InputLabel>
                          <TextField
                            disabled={
                              !isAdding &&
                              !isGlobal &&
                              (editingRecord ? editingRecord.isGlobal : false)
                            }
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            sx={{
                              width: 300,
                              marginTop: 1,
                              marginBottom: 2,
                              marginRight: 5,
                            }}
                          />
                        </Stack>
                        <Stack>
                          <InputLabel
                            className="conn-pair-options"
                            style={{
                              paddingRight: "10px",
                              paddingBottom: "5px",
                            }}
                          >
                            Script Runs:
                          </InputLabel>
                          <Autocomplete
                            options={options}
                            disabled={
                              !isGlobal &&
                              (!isAdding && editingRecord
                                ? editingRecord.isGlobal
                                : false)
                            }
                            value={options.find((o) => o.value === timing) ?? 0}
                            onChange={(e, newValue) => {
                              setTiming(newValue?.value ?? 0);
                            }}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                              <TextField
                                sx={{
                                  marginTop: 1,
                                  marginBottom: 2,
                                  width: 250,
                                }}
                                {...params}
                                autoComplete="off"
                              />
                            )}
                          />
                        </Stack>
                      </Stack>
                      <Typography
                        className="conn-pair-options"
                        style={{
                          paddingRight: "10px",
                          paddingBottom: "15px",
                          fontSize: "0.95rem",
                        }}
                      >
                        Date Last Updated:{" "}
                        {!dateUpdated
                          ? ""
                          : dayjs(dateUpdated)
                              .local()
                              .format("YYYY.MM.DD HH:mm")}
                      </Typography>
                      <Typography
                        className="conn-pair-options"
                        style={{ paddingRight: "10px", fontSize: "0.95rem" }}
                      >
                        Updated By: {updatedBy}
                      </Typography>
                      {
                        <Box
                          style={{
                            marginTop: 24,
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <ScriptEditor
                            height="300px"
                            value={script}
                            onChange={(val, _) => {
                              if (
                                !isAdding &&
                                !isGlobal &&
                                (editingRecord ? editingRecord.isGlobal : false)
                              )
                                return;
                              setScript(val);
                            }}
                            variableCompletions={autocompletions}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row-reverse",
                              marginTop: "10px",
                              paddingRight: "10px",
                            }}
                          >
                            <Button
                              className="btn-primary"
                              onClick={handleSave}
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      }
                    </Stack>
                  </Container>
                </Box>
              </div>
            </SettingsCard>
            <PreviewWorkflowScript
              isGlobal={isGlobal}
              onClose={onClose}
              workflowName={workflowName}
              scripts={previewRows}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                marginTop: "20px",
              }}
            >
              <Button className="btn-primary" onClick={handleSave}>
                Save
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: "var(--primary-main)",
                  color: "var(--primary-main)",
                  p: "20px",
                  marginRight: "10px",
                  borderRadius: "10px",
                  height: "57px",
                }}
                onClick={handleClose}
              >
                Close
              </Button>
            </Box>
          </>
        </Stack>
      }
    </>
  );
};

export default WorkflowScriptSettings;
